import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import {
  FormattedMessage,
  MessageDescriptor,
  defineMessages,
  useIntl,
} from "react-intl";
import { UpdateUserRoleBodyRole, User } from "~/api/model";
import { getListUsersQueryKey, useUpdateUserRole } from "~/api/users/users";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useSettingsParams } from "~/helpers";

interface ChangeRoleDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User;
}

const roles: Record<UpdateUserRoleBodyRole, MessageDescriptor> = defineMessages(
  {
    OWNER: {
      id: "settings.team.role.owner",
      defaultMessage: "Admin",
    },
    MEMBER: {
      id: "settings.team.role.member",
      defaultMessage: "Member",
    },
  }
);

enum ChangeRoleDialogStep {
  RoleSelection = 1,
  ChangeRoleConfirmation = 2,
  Success = 3,
}

export const ChangeRoleDialog: FC<ChangeRoleDialogProps> = ({
  open,
  setOpen,
  user,
}) => {
  const { organizationId } = useSettingsParams();
  const intl = useIntl();

  const [step, setStep] = useState<ChangeRoleDialogStep>(
    ChangeRoleDialogStep.RoleSelection
  );

  const getRoles = (): UpdateUserRoleBodyRole[] => {
    const allRoles: UpdateUserRoleBodyRole[] = ["OWNER", "MEMBER"];
    return allRoles.filter((role) => role !== user.role);
  };

  const [selectedRole, setSelectedRole] = useState<UpdateUserRoleBodyRole>(
    getRoles()[0]
  );

  const handleSave = () => {
    if (selectedRole === "OWNER") {
      setStep(ChangeRoleDialogStep.ChangeRoleConfirmation);
    } else {
      submit();
    }
  };

  const { mutate: updateUserRole, isPending } = useUpdateUserRole();
  const queryClient = useQueryClient();

  const submit = () => {
    const body = {
      role: selectedRole as UpdateUserRoleBodyRole,
    };

    updateUserRole(
      {
        organizationId,
        userId: user.id,
        data: body,
      },
      {
        onSuccess: () => {
          setStep(ChangeRoleDialogStep.Success);
          queryClient.invalidateQueries({
            queryKey: getListUsersQueryKey(organizationId),
          });
        },
        onError: () => {
          setOpen(false);
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {step === ChangeRoleDialogStep.Success ? (
              <FormattedMessage id="success" defaultMessage="Success" />
            ) : (
              <FormattedMessage
                id="settings.team.changeRole"
                defaultMessage="Change Role"
              />
            )}
          </AlertDialogTitle>
          {step === ChangeRoleDialogStep.ChangeRoleConfirmation && (
            <AlertDialogDescription>
              <FormattedMessage
                id="settings.team.roles.confirmation"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  userName: user.name,
                  userEmail: user.email,
                }}
              />
              <div className="h-5" />
              <FormattedMessage
                id="settings.team.roles.capabilities"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  ul: (chunks) => <ul>{chunks}</ul>,
                  li: (chunks) => <li>{chunks}</li>,
                }}
              />
            </AlertDialogDescription>
          )}
          {step === ChangeRoleDialogStep.Success && (
            <AlertDialogDescription>
              <FormattedMessage
                id="settings.team.roles.success"
                values={{
                  userName: user.name,
                  role: intl.formatMessage(roles[selectedRole]),
                }}
              />
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {step === ChangeRoleDialogStep.RoleSelection && (
          <div className="flex flex-col gap-4">
            <div className="text-sm text-muted-foreground">
              <FormattedMessage
                id="settings.team.currentRole"
                defaultMessage="Current Role:"
              />{" "}
              {intl.formatMessage(roles[user.role as UpdateUserRoleBodyRole])}
            </div>
            <div className="flex flex-col gap-2">
              <Label>
                <FormattedMessage
                  id="settings.team.role"
                  defaultMessage="New Role"
                />
              </Label>
              <Select
                onValueChange={(value) =>
                  setSelectedRole(value as UpdateUserRoleBodyRole)
                }
                value={selectedRole}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {getRoles().map((role) => (
                      <SelectItem key={role} value={role}>
                        {intl.formatMessage(roles[role])}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {step === ChangeRoleDialogStep.Success ? (
                <FormattedMessage id="close" defaultMessage="Close" />
              ) : (
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              )}
            </Button>
            {step === ChangeRoleDialogStep.RoleSelection && (
              <Button onClick={handleSave} disabled={isPending}>
                <FormattedMessage id="change" defaultMessage="Change" />
              </Button>
            )}
            {step === ChangeRoleDialogStep.ChangeRoleConfirmation && (
              <Button onClick={submit} disabled={isPending}>
                <FormattedMessage id="continue" defaultMessage="Continue" />
              </Button>
            )}
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
